import React, { useState } from "react";
import withRouter from "@components/common/withRouter";
import * as z from "zod";
import { IForgotPassword } from "@interfaces/auth";
import { MailIcon } from "lucide-react";

import { auth } from "@/services/auth/auth-service";
import { handleApiError } from "@/services/api";
import toast from "react-hot-toast";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { Formik, FormikHelpers } from "formik";
import { Button, Input, Link } from "@heroui/react";
import { Icon } from "@iconify/react";
import { AuthHeader } from "@/layouts/non-auth-layout";

const ForgotPasswordSuccess = () => {
  return (
    <>
      <div className="text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100 text-green-600">
          <MailIcon className="h-6 w-6" />
        </div>
        <h2 className="text-xl font-medium mt-5">
          Check your email
        </h2>
        <p className="text-sm text-default-500 m-2">
          We sent an email with password reset instructions.
        </p>
        <p className="text-xs text-default-400">
          Ps. make sure to check your spam or promotions folder.
        </p>
      </div>
    </>
  );
};

const ForgotPasswordSchema = z.object({
  email: z.string().min(1, "Email is required").email("Email is invalid"),
});

const ForgotPassword = () => {
  document.title = "Forgot Password";

  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState(false);

  const onSubmit = async (
    values: IForgotPassword,
    { setSubmitting }: FormikHelpers<any>
  ) => {
    try {
      setSubmitting(true);
      await auth.forgotPassword(values);
      setForgotPasswordSuccess(true);
    } catch (e) {
      console.log(handleApiError(e));
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return forgotPasswordSuccess ? (
    <ForgotPasswordSuccess />
  ) : (
    <React.Fragment>
      <AuthHeader
        title="Forgot password?"
        subtitle=" No worries, we'll send you reset instructions." />

      <Formik
        initialValues={{
          email: "",
        }}
        onSubmit={onSubmit}
        validationSchema={toFormikValidationSchema(ForgotPasswordSchema)}>
        {({
          errors,
          touched,
          handleSubmit,
          handleChange,
          isSubmitting,
          values,
        }) => (
          <>
            <Input
              id="email"
              label="Email Address"
              name="email"
              placeholder="Enter your work email"
              type="email"
              variant="bordered"
              value={values.email}
              onChange={handleChange}
              isInvalid={errors?.email != null}
              errorMessage={touched?.email && errors?.email ? errors.email : ""} />
            <Button
              type="submit"
              isLoading={isSubmitting}
              onClick={() => handleSubmit()}
              style={{ backgroundColor: "#c6f648" }}>
              Continue
            </Button>
          </>
        )}
      </Formik>
      <p className="text-center">
        <Link href="/login" size="sm" className="text-default-500">
          <Icon icon="ic:round-arrow-back" className="mr-2" />
          Go back to login
        </Link>
      </p>
    </React.Fragment>
  );
};

export default withRouter(ForgotPassword);
